import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useWebSocketStore } from './useWebSocketStore'
import { useChatStore } from './useChatStore'
import { useMessageStore } from './useMessageStore'
import { useContactStore } from './useContactStore'
import { useNotifications } from '@/composables/useNotifications'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCampaignRegistrationStore } from '@/stores/useCampaignRegistrationStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'
import { useCampaignRegistrationSync } from '@/composables/useCampaignRegistrationSync'
import { useTabSessionStore } from '@/stores/useTabSessionStore'
import { createChatEventHandlers } from '../services/chatEventHandlers'
import { createMessageEventHandlers } from '../services/messageEventHandlers'
import { createChatOperations } from '../services/chatOperations'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type {
  MessageEventData,
  MessageStatusEventData,
  UserSendTemplateMessageEvent,
  NewChatEvent,
  NewChatTemplateEvent,
  UserEntryChatEvent,
  UserTookChatEvent,
  UserExitChatEvent,
  ChatFinishedEvent,
  VisitorTypingEvent,
  TransferRequestEvent,
  TransferAcceptedEvent,
  ChatTransferredToCampaignEvent,
  ChatTransferredToBotEvent,
  ChatUpdatedEvent,
  ChatClosedByTimeoutClientEvent,
  ChatReleasedByUserTimeoutEvent,
  ChatReleasedByAgentLogoutEvent,
  ChatReleasedByBulkTemplateEvent,
  ChatExpiredByMetaWindowEvent,
  TemplateSendBlockedEvent,
  TemplateSendFailedEvent,
  AgentEjectedFromCampaignsEvent,
  AgentPauseChangedBySupervisorEvent,
} from '../types/interfaces'
import { MessageSourceType, UserRole } from '../types/enums'

export const useWhatsAppCoordinator = defineStore('whatsapp-coordinator', () => {
  const webSocketStore = useWebSocketStore()
  const chatStore = useChatStore()
  const messageStore = useMessageStore()
  const contactStore = useContactStore()
  const notifications = useNotifications()
  const authStore = useAuthStore()
  const tabSession = useTabSessionStore()
  const campaignRegistration = useCampaignRegistrationStore()
  const pauseStatus = usePauseStatusStore()
  const { refreshChatRegistration } = useCampaignRegistrationSync()

  const isInitialized = ref(false)
  let initializationPromise: Promise<void> | null = null
  let eventHandlersBound = false

  const chatEventHandlers = createChatEventHandlers(chatStore, notifications)
  const messageEventHandlers = createMessageEventHandlers(messageStore, chatStore, notifications)
  const chatOperations = createChatOperations(webSocketStore, chatStore, authStore)

  const initialize = async (userId: string, role: UserRole = UserRole.OPERATOR) => {
    if (isInitialized.value && webSocketStore.isConnected) {
      return
    }

    if (initializationPromise) {
      return initializationPromise
    }

    initializationPromise = (async () => {
      chatStore.currentUserId = userId
      chatStore.currentUserRole = role

      await webSocketStore.initializeWebSocket(userId, role)
      setupEventHandlers()
      isInitialized.value = true
    })()

    try {
      await initializationPromise
    } catch (error) {
      isInitialized.value = false
      throw error
    } finally {
      initializationPromise = null
    }
  }

  const setupEventHandlers = () => {
    if (eventHandlersBound) {
      return
    }

    // El backend cerró este socket porque el mismo agente se conectó en otro
    // sitio. Quién fue decide qué hacer:
    //
    // - Otra pestaña de este navegador: la sesión del agente sigue viva (mismo
    //   token, mismo user_log) y solo perdimos el puesto. Se cede y ya. Cerrar
    //   aquí sería destructivo: `localStorage` es compartido y dejaríamos sin
    //   credenciales a la pestaña que sí está trabajando.
    // - Otro navegador o equipo: la sesión de aquí ya está cerrada en la BD y
    //   fuera de las colas de Asterisk, así que se limpia en local y se avisa.
    //   Un logout contra el backend cerraría la sesión del que acaba de entrar.
    webSocketStore.onConnectionStateChange(({ state }) => {
      // La sesion se cerro en el backend (watchdog o logout) y solo queda
      // limpiar en local. No hay pestana hermana que consultar: el backend
      // responde por la fila de user_logs, que es la misma para todas.
      if (state === 'session_expired') {
        authStore.closeInvalidSession({ notice: 'session_expired' })
        return
      }

      if (state === 'unauthorized') {
        authStore.closeInvalidSession()
        return
      }

      if (state !== 'session_replaced') return

      void tabSession.yieldIfSiblingActive().then((yielded) => {
        if (yielded) return
        authStore.closeInvalidSession({ notice: 'session_replaced' })
      })
    })

    webSocketStore.on('MESSAGE_EVENT', (data) => {
      if ('content' in data && 'chat_id' in data) {
        const messageEvent = data as MessageEventData
        messageEventHandlers.handleMessageEvent(messageEvent)

        // Con el chat abierto el agente ya lo esta viendo, asi que el contador
        // local no sube; la marca del servidor tiene que avanzar igual o estos
        // mensajes volverian como no leidos en la proxima carga.
        if (
          chatStore.selectedChatId === messageEvent.chat_id &&
          messageEvent.content?.source_type === MessageSourceType.ENTRANTE
        ) {
          chatOperations.markChatRead(messageEvent.chat_id)
        }
      }
    })

    webSocketStore.on('MESSAGE_STATUS_EVENT', (data) => {
      if ('message_id' in data && 'status' in data) {
        messageStore.handleMessageStatusEvent(data as MessageStatusEventData)
      }
    })

    webSocketStore.on('USER_SEND_TEMPLATE_MESSAGE', (data) => {
      if ('chat_id' in data && 'template_message' in data) {
        messageEventHandlers.handleUserSendTemplateMessage(data as UserSendTemplateMessageEvent)
      }
    })

    webSocketStore.on('NEW_CHAT', (data) => chatEventHandlers.handleNewChat(data as NewChatEvent))
    webSocketStore.on('NEW_CHAT_TEMPLATE', (data) =>
      chatEventHandlers.handleNewChatTemplate(data as NewChatTemplateEvent),
    )
    webSocketStore.on('USER_ENTRY_CHAT', (data) => chatEventHandlers.handleUserEntryChat(data as UserEntryChatEvent))
    webSocketStore.on('USER_TOOK_CHAT', (data) => chatEventHandlers.handleUserTookChat(data as UserTookChatEvent))
    webSocketStore.on('USER_EXIT_TO_CHAT', (data) => chatEventHandlers.handleUserExitChat(data as UserExitChatEvent))
    webSocketStore.on('CHAT_FINISHED', (data) => chatEventHandlers.handleChatFinished(data as ChatFinishedEvent))
    webSocketStore.on('VISITOR_TYPING', (data) => chatEventHandlers.handleVisitorTyping(data as VisitorTypingEvent))
    webSocketStore.on('REQUEST_TRANSFER', (data) => chatEventHandlers.handleTransferRequest(data as TransferRequestEvent))
    webSocketStore.on('TRANSFER_ACCEPTED', (data) => chatEventHandlers.handleTransferAccepted(data as TransferAcceptedEvent))
    webSocketStore.on('CHAT_TRANSFERRED_TO_CAMPAIGN', (data) => chatEventHandlers.handleChatTransferredToCampaign(data as ChatTransferredToCampaignEvent))
    webSocketStore.on('CHAT_TRANSFERRED_TO_BOT', (data) => chatEventHandlers.handleChatTransferredToBot(data as ChatTransferredToBotEvent))
    webSocketStore.on('CHAT_UPDATED', (data) => chatEventHandlers.handleChatUpdated(data as ChatUpdatedEvent)) // Evento sin definir en el backend, pero preparado para futuros cambios
    webSocketStore.on('CHAT_CLOSED_BY_TIMEOUT_CLIENT', (data) => chatEventHandlers.handleChatClosedByTimeout(data as ChatClosedByTimeoutClientEvent))
    webSocketStore.on('CHAT_RELEASED_BY_USER_TIMEOUT', (data) => chatEventHandlers.handleChatReleasedByUserTimeout(data as ChatReleasedByUserTimeoutEvent))
    webSocketStore.on('CHAT_RELEASED_BY_USER_LOGOUT', (data) => chatEventHandlers.handleChatReleasedByAgentLogout(data as ChatReleasedByAgentLogoutEvent))
    webSocketStore.on('CHAT_RELEASED_BY_BULK_TEMPLATE', (data) => chatEventHandlers.handleChatReleasedByBulkTemplate(data as ChatReleasedByBulkTemplateEvent))
    webSocketStore.on('CHAT_EXPIRED_BY_META_WINDOW', (data) => chatEventHandlers.handleChatExpiredByMetaWindow(data as ChatExpiredByMetaWindowEvent))

    webSocketStore.on('TEMPLATE_SEND_BLOCKED', (data) => {
      if ('chat_id' in data && 'attending_users' in data) {
        handleTemplateSendBlocked(data as TemplateSendBlockedEvent)
      }
    })

    webSocketStore.on('TEMPLATE_SEND_FAILED', (data) => {
      if ('reason' in data) {
        handleTemplateSendFailed(data as TemplateSendFailedEvent)
      }
    })

    webSocketStore.on('AGENT_EJECTED_FROM_CAMPAIGNS', (data) => {
      if ('user_id' in data) {
        handleAgentEjectedFromCampaigns(data as AgentEjectedFromCampaignsEvent)
      }
    })

    webSocketStore.on('AGENT_PAUSE_CHANGED_BY_SUPERVISOR', (data) => {
      if ('user_id' in data && 'paused' in data) {
        handleAgentPauseChangedBySupervisor(data as AgentPauseChangedBySupervisorEvent)
      }
    })

    eventHandlersBound = true
  }

  // El supervisor sacó al agente de todas sus campañas desde el monitor. El
  // webphone escucha lo mismo por el canal de monitoreo, pero ese va filtrado
  // por extensión y el agente de solo chat no tiene ninguna, así que para él
  // este es el único aviso. Los dos caminos dejan el mismo estado, de modo que
  // a quien le lleguen ambos ve un solo diálogo.
  const handleAgentEjectedFromCampaigns = (data: AgentEjectedFromCampaignsEvent) => {
    // Un ADMIN conectado recibe todo evento sin filtrar por chat_users, así que
    // sin esta comprobación vería el aviso de la expulsión de otro.
    if (data.user_id !== chatStore.currentUserId) return

    campaignRegistration.showEjectionNotice()
    pauseStatus.clearPause()

    // El backend cierra el campaign_logins ANTES de publicar, así que la
    // reconsulta ve la fila ya cerrada y el agente cae a "no registrado" en vez
    // de quedarse con el último valor conocido.
    campaignRegistration.expectVoiceUnregistration()
    void refreshChatRegistration()
  }

  // El supervisor pausó o reanudó al agente desde el monitor. Igual que la
  // expulsión, el webphone escucha lo mismo por el canal de monitoreo, pero ese
  // va filtrado por extensión y el agente de solo chat no tiene ninguna.
  // `applyPauseFromAsterisk` reconcilia por el motivo, así que recibir los dos
  // avisos no duplica nada.
  const handleAgentPauseChangedBySupervisor = (data: AgentPauseChangedBySupervisorEvent) => {
    if (data.user_id !== chatStore.currentUserId) return

    if (data.paused) pauseStatus.applyPauseFromAsterisk(data.reason)
    else pauseStatus.clearPause()
  }

  // Fallback del pre-chequeo REST: si entre el chequeo y el envío el chat quedó
  // reservado (carrera), el backend rechaza el envío y avisa por este evento.
  const handleTemplateSendBlocked = (data: TemplateSendBlockedEvent) => {
    const attendingNames = data.attending_users.map((user) => user.name || user.id).join(', ')
    notifications.notifySystemAction({
      title: 'Envío de plantilla bloqueado',
      body: data.reserved_by_me
        ? 'El chat está reservado por ti y el destino elegido te retiraría de la atención. Reintenta el envío para confirmarlo.'
        : `El chat está siendo atendido por ${attendingNames}. Reintenta el envío para confirmar la toma del chat.`,
      chatId: data.chat_id,
    })
  }

  // El envío (individual, masivo o toma de chat existente) no se completó:
  // el proveedor lo rechazó, la cuenta usada no tiene incoming route
  // configurada, o la reactivación apuntaba a un chat que ya no está vigente.
  // No se creó ni actualizó ningún chat para el/los destino(s).
  const handleTemplateSendFailed = (data: TemplateSendFailedEvent) => {
    // Resumen agregado del masivo: un solo aviso con el conteo, no uno por
    // contacto. El detalle de cuáles contactos fallaron queda pendiente.
    if (data.failed_count != null) {
      notifications.notifySystemAction({
        title: 'Envío masivo con errores',
        body: `${data.failed_count} de ${data.total_count} plantillas no se pudieron enviar.`,
        tag: 'template-send-failed-bulk-summary',
      })
      return
    }

    const bodyByReason: Record<string, string> = {
      MISSING_INCOMING_ROUTE: `Sin ruta configurada — no se envió a ${data.phone}.`,
      // El chat que se quería reactivar se cerró, o dejó de ser el chat vigente
      // del número: el cliente ya reabrió la conversación por otro lado.
      CHAT_NOT_REACTIVABLE:
        'El chat ya no se puede reactivar: fue cerrado o el cliente retomó la conversación en otro chat.',
    }
    const body = bodyByReason[data.reason] ?? `El proveedor rechazó el envío a ${data.phone}.`

    notifications.notifySystemAction({
      title: 'Envío de plantilla no completado',
      body,
      tag: `template-send-failed-${data.destination_id}`,
    })
  }

  const setCampaignMap = (campaigns: Map<string, IOptionsCampaign>) => {
    chatStore.setCampaignMap(campaigns)
  }

  const disconnect = () => {
    eventHandlersBound = false
    webSocketStore.disconnect()
    chatStore.clearChats()
    messageStore.clearSendingMessages()
    contactStore.clearContactCache()
    isInitialized.value = false
  }

  return {
    webSocketStore,
    chatStore,
    messageStore,
    contactStore,
    initialize,
    disconnect,
    setCampaignMap,
    ...chatOperations,
  }
})
